import { AlignmentSettings } from '@trimble/types';
import { needsFullAlignmentReload } from './DataContextProviderHelpers';

export type ReloadType = 'trajectory' | 'styled-trajectory';

/**
 * Schedules alignment reload operations with proper debouncing.
 *
 * Full API reloads (`trajectory`) and local retransforms (`styled-trajectory`)
 * have different debounce windows.  When a full reload is pending and a
 * retransform-only change arrives, the retransform is delayed to fire after
 * the full reload so the updated trimbim is available — both go through the
 * same queue, guaranteeing ordering.
 *
 * `needsFullAlignmentReload` is always evaluated against the last *applied*
 * snapshot (the snapshot whose timer has actually fired), not the last *seen*
 * snapshot.  This prevents a fast retransform change from silently cancelling
 * a necessary full reload.
 */
export class AlignmentReloadScheduler {
  // eslint-disable-next-line no-magic-numbers
  private static readonly FULL_RELOAD_MS = 1000;
  // eslint-disable-next-line no-magic-numbers
  private static readonly RETRANSFORM_MS = 300;

  private fullReloadTimer: ReturnType<typeof setTimeout> | undefined;
  private retransformTimer: ReturnType<typeof setTimeout> | undefined;
  private pendingFullReload = false;
  private lastAppliedSnapshot: AlignmentSettings | undefined; // undefined until first schedule fires
  private latestLoadedItems: string[] = [];

  constructor(
    private readonly executeReload: (type: ReloadType, items: string[]) => void,
    private readonly fullReloadMs = AlignmentReloadScheduler.FULL_RELOAD_MS,
    private readonly retransformMs = AlignmentReloadScheduler.RETRANSFORM_MS
  ) {}

  public schedule(
    settings: AlignmentSettings,
    prev: AlignmentSettings | undefined,
    loadedItems: string[]
  ): void {
    this.latestLoadedItems = loadedItems;

    const baseline = this.pendingFullReload ? this.lastAppliedSnapshot : prev;

    const fullReload = needsFullAlignmentReload(settings, baseline);

    if (fullReload) {
      clearTimeout(this.retransformTimer);
      clearTimeout(this.fullReloadTimer);
      this.pendingFullReload = true;
      this.fullReloadTimer = setTimeout(() => {
        this.pendingFullReload = false;
        this.lastAppliedSnapshot = settings;
        this.executeReload('trajectory', this.latestLoadedItems);
      }, this.fullReloadMs);
    } else if (this.pendingFullReload) {
      clearTimeout(this.retransformTimer);
      this.retransformTimer = setTimeout(() => {
        this.lastAppliedSnapshot = settings;
        this.executeReload('styled-trajectory', this.latestLoadedItems);
      }, this.fullReloadMs);
    } else {
      clearTimeout(this.retransformTimer);
      this.retransformTimer = setTimeout(() => {
        this.lastAppliedSnapshot = settings;
        this.executeReload('styled-trajectory', this.latestLoadedItems);
      }, this.retransformMs);
    }
  }

  public dispose(): void {
    clearTimeout(this.fullReloadTimer);
    clearTimeout(this.retransformTimer);
  }
}
